import React, { type FC, useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

import { UserAPIs } from '@/api';
import { logout } from '@/api/base/auth';
import { type User, UserType } from '@/api/types/user';
import { AccountSettingsModal } from '@/common/account-settings-modal';
import { AdminControlsModal } from '@/common/admin-controls-modal';
import { IntaglioIcon } from '@/common/intaglio-icon';
import { Navbar } from '@/common/navbar';
import { QuickHelp } from '@/common/quick-help';
import { TabBar, type TabItem } from '@/common/tab-bar';
import { UserMenu } from '@/common/user-menu';

const TABS: TabItem[] = [
    { name: 'Template', id: 'templates' },
    { name: 'Creative', id: 'creatives' },
];

function tabToPath(tab: string): string {
    let path = '';
    switch (tab) {
        case 'templates':
            path = '/templates';
            break;
        case 'creatives':
            path = '/creatives';
            break;
    }
    return path;
}

function pathToTab(path: string): string {
    let tab = '';
    switch (path) {
        case '/templates':
            tab = 'templates';
            break;
        case '/creatives':
            tab = 'creatives';
            break;
    }
    return tab;
}

export const HomeLayout: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [currentTab, setCurrentTab] = useState<string>(pathToTab(location.pathname));
    const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState<boolean>(false);
    const [isAdminControlsOpen, setIsAdminControlsOpen] = useState<boolean>(false);
    const [user, setUser] = useState<User | null>(null);

    const handleTabChange = useCallback(
        (tabId: string) => {
            const path = tabToPath(tabId);
            if (path) {
                navigate(path);
            }
            setCurrentTab(tabId);
        },
        [navigate],
    );

    const fetchUser = useCallback((refetch: boolean) => {
        UserAPIs.getCurrentUser(refetch).then((response) => {
            setUser(response);
        });
    }, []);

    useEffect(() => {
        if (user?.type === UserType.MEMBER) {
            navigate('/creatives');
        }
    }, [user, navigate]);

    useEffect(() => {
        fetchUser(false);
    }, [fetchUser]);

    return (
        <>
            <div className="flex flex-col h-screen w-screen bg-black-50">
                <Navbar
                    leading={
                        <Link to="/">
                            <IntaglioIcon />
                        </Link>
                    }
                    center={
                        user?.type !== UserType.MEMBER && (
                            <TabBar selectedTab={currentTab} onTabChange={handleTabChange} tabs={TABS} />
                        )
                    }
                    trailing={
                        <div className="flex flex-row gap-2.5 items-center">
                            <QuickHelp />
                            <div className="h-6 border-gray-100 border-l-[1px]" />
                            <UserMenu
                                user={user}
                                onLogout={() => {
                                    logout();
                                    navigate('/login', { replace: true });
                                }}
                                onAccountSettings={() => setIsAccountSettingsOpen(true)}
                                onOrganizationSettings={() => setIsAdminControlsOpen(true)}
                            />
                        </div>
                    }
                />

                <Outlet />
            </div>

            <AccountSettingsModal
                isOpen={isAccountSettingsOpen}
                setIsOpen={setIsAccountSettingsOpen}
                onAccountUpdated={() => fetchUser(true)}
            />
            {user?.type === UserType.ADMIN && (
                <AdminControlsModal
                    isOpen={isAdminControlsOpen}
                    setIsOpen={setIsAdminControlsOpen}
                    onAccountUpdated={() => fetchUser(true)}
                />
            )}
        </>
    );
};
